import type { Components } from '@/api/generated/api';
import type { AnalyticsUserProperties } from '@/services/analytics';

export type AnalyticsBillingState = 'anonymous' | 'basic' | 'paid' | 'trialing' | 'unlocked';
export type AnalyticsBillingProduct = Components.Schemas.Product | 'multiple' | 'none';
export type AnalyticsBillingInterval = Components.Schemas.BillingInterval | 'mixed' | 'none';

export type BillingStateUserProperties = AnalyticsUserProperties & {
  billing_interval: AnalyticsBillingInterval;
  billing_product: AnalyticsBillingProduct;
  billing_state: AnalyticsBillingState;
};

export const ANONYMOUS_BILLING_USER_PROPERTIES: BillingStateUserProperties = {
  billing_interval: 'none',
  billing_product: 'none',
  billing_state: 'anonymous',
};

export type BillingStateUserPropertiesContext = {
  billingStatus: Components.Schemas.BillingStatusDto | undefined;
  hasCurrentTenant: boolean;
  isLoggedIn: boolean;
};

function getActiveProducts(
  billingStatus: Components.Schemas.BillingStatusDto,
): Components.Schemas.BillingStatusProductDto[] {
  return billingStatus.products.filter((product) => product.isCurrentlyActive);
}

function resolveBillingProduct(activeProducts: Components.Schemas.BillingStatusProductDto[]): AnalyticsBillingProduct {
  const products = [...new Set(activeProducts.map((product) => product.product))].sort();
  if (products.length === 0) return 'none';
  if (products.length === 1) return products[0];
  return 'multiple';
}

function resolveBillingInterval(
  activeProducts: Components.Schemas.BillingStatusProductDto[],
): AnalyticsBillingInterval {
  const intervals = [...new Set(activeProducts.map((product) => product.billingInterval))].sort();
  if (intervals.length === 0) return 'none';
  if (intervals.length === 1) return intervals[0];
  return 'mixed';
}

export function buildBillingStateUserProperties(
  billingStatus: Components.Schemas.BillingStatusDto,
): BillingStateUserProperties {
  const activeProducts = getActiveProducts(billingStatus);
  const baseProperties = {
    billing_interval: resolveBillingInterval(activeProducts),
    billing_product: resolveBillingProduct(activeProducts),
  };

  if (!billingStatus.isBillingActive) {
    return {
      ...baseProperties,
      billing_state: 'unlocked',
    };
  }

  if (billingStatus.isTrialActive) {
    return {
      ...baseProperties,
      billing_state: 'trialing',
    };
  }

  if (activeProducts.length > 0) {
    return {
      ...baseProperties,
      billing_state: 'paid',
    };
  }

  return {
    ...baseProperties,
    billing_state: 'basic',
  };
}

export function buildBillingAnalyticsUserProperties(
  context: BillingStateUserPropertiesContext,
): BillingStateUserProperties | null {
  if (!context.isLoggedIn || !context.hasCurrentTenant) {
    return ANONYMOUS_BILLING_USER_PROPERTIES;
  }

  if (!context.billingStatus) {
    return null;
  }

  return buildBillingStateUserProperties(context.billingStatus);
}
